<template>
  <!-- Line mode: plain text at the inherited size and colour, no labels. Defaults collapse
       to the source alone ("Booking site"); tracked records read "Google · Cpc · Spring sale ·
       google.com". Full labelled detail in the light tooltip on hover. -->
  <span v-if="mode === 'line'" class="utm-attribution utm-attribution--line">
    <span v-if="state === 'none'" class="utm-attribution__empty">{{ emptyText }}</span>
    <span v-else-if="!hasDetail" class="badge badge-default utm-attribution__badge"><i v-if="icon" class="tim-icons" :class="icon"></i>{{ chipLabel }}</span>
    <el-tooltip v-else effect="light" placement="top" :open-delay="300" popper-class="utm-attribution__tip">
      <template #content>
        <div>
          <div v-for="row in rows" :key="row.key" class="utm-attribution__tip-row">
            <span class="utm-attribution__tip-label">{{ row.label }}</span>
            <span class="utm-attribution__tip-value" :class="{ 'utm-attribution__tip-value--raw': row.raw }">{{ row.value }}</span>
          </div>
        </div>
      </template>
      <span class="utm-attribution__line utm-attribution__hover">
        <template v-for="(seg, i) in lineSegments">
          <span v-if="i > 0" :key="seg.key + '-sep'" class="utm-attribution__sep">&middot;</span>
          <span v-if="i === 0" :key="seg.key" class="badge badge-default utm-attribution__badge"><i v-if="icon" class="tim-icons" :class="icon"></i>{{ seg.value }}</span>
          <span v-else :key="seg.key" :class="{ 'utm-attribution__line-ref': seg.key === 'referrer' }">{{ seg.value }}</span>
        </template>
      </span>
    </el-tooltip>
  </span>

  <!-- Chip mode: compact source chip, full attribution in the same light tooltip -->
  <span v-else class="utm-attribution utm-attribution--chip">
    <span v-if="state === 'none'" class="utm-attribution__empty">{{ emptyText }}</span>
    <span v-else-if="!hasDetail" class="badge badge-default utm-attribution__badge">
      <i v-if="icon" class="tim-icons" :class="icon"></i>{{ chipLabel }}
    </span>
    <el-tooltip v-else effect="light" placement="top" :open-delay="300" popper-class="utm-attribution__tip">
      <template #content>
        <div>
          <div v-for="row in rows" :key="row.key" class="utm-attribution__tip-row">
            <span class="utm-attribution__tip-label">{{ row.label }}</span>
            <span class="utm-attribution__tip-value" :class="{ 'utm-attribution__tip-value--raw': row.raw }">{{ row.value }}</span>
          </div>
        </div>
      </template>
      <span class="badge badge-default utm-attribution__badge utm-attribution__hover">
        <i v-if="icon" class="tim-icons" :class="icon"></i>{{ chipLabel }}
      </span>
    </el-tooltip>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  utmState, utmChipLabel, utmDetailRows, utmHasDetail, utmLineSegments,
} from '@/util/utmAttribution';

const props = defineProps({
  source: { type: String, default: '' },
  medium: { type: String, default: '' },
  campaign: { type: String, default: '' },
  referrer: { type: String, default: '' },
  // 'chip' for list rows and cards, 'line' for detail pages and the booking drawer
  mode: { type: String, default: 'chip' },
  // Optional tim-icons class (without the tim-icons prefix) shown before the chip label
  icon: { type: String, default: '' },
  // Rendered when there is no UTM row at all; empty string renders nothing
  emptyText: { type: String, default: '' },
});

const utm = computed(() => ({
  source: props.source,
  medium: props.medium,
  campaign: props.campaign,
  referrer: props.referrer,
}));

const state = computed(() => utmState(utm.value));
const chipLabel = computed(() => utmChipLabel(utm.value));
const rows = computed(() => utmDetailRows(utm.value));
const hasDetail = computed(() => utmHasDetail(utm.value));
const lineSegments = computed(() => utmLineSegments(utm.value));
</script>

<script lang="ts">
import { Tooltip } from 'element-ui';
export default {
  name: 'utm-attribution',
  components: { [Tooltip.name]: Tooltip },
};
</script>

<style scoped lang="scss">
/* Line mode deliberately sets no colour or size: it inherits from the parent
   (card <p>, drawer .detail-row) so it reads like its sibling lines. */
.utm-attribution--line {
  display: inline-flex;
  align-items: baseline;
  min-width: 0;
}

.utm-attribution__line {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 6px;
  min-width: 0;
}

.utm-attribution__sep {
  opacity: 0.5;
}

.utm-attribution__line-ref {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.utm-attribution--chip {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

/* Theme .badge does the colours in both themes; only the icon spacing is ours.
   The theme badge carries margin-bottom: 5px for stacked use; inline next to text
   that reads as a gap under every badge, so it is zeroed here. */
.utm-attribution__badge {
  margin-bottom: 0;
}

.utm-attribution__badge i {
  font-size: 11px;
  margin-right: 4px;
  vertical-align: -1px;
}

.utm-attribution__empty {
  opacity: 0.6;
}

/* Same pointer the app uses on other hover-detail targets (TagDots). */
.utm-attribution__hover {
  cursor: pointer;
}
</style>

<style lang="scss">
/* Tooltip content renders outside the component root, so these are unscoped.
   effect="light" gives the white panel with dark text used across the app. */
.el-tooltip__popper.is-light.utm-attribution__tip {
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.5;
}
.utm-attribution__tip-row {
  display: flex;
  gap: 12px;
  align-items: baseline;
  white-space: nowrap;
}
.utm-attribution__tip-label {
  flex-shrink: 0;
  min-width: 64px;
  color: #8a8a8a;
}
.utm-attribution__tip-value {
  color: #222a42;
  font-weight: 500;
}
.utm-attribution__tip-value--raw {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
}
</style>
